import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Facebook, Instagram, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { BOOKING_URL, NAV_LINKS, goToLink } from "@/lib/site";
import { SITE } from "@/content/site";
import { useLang } from "@/i18n";
import { IMAGES } from "@/content/images";

const Footer = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useLang();
  const { contact } = SITE;

  return (
    <footer data-testid="site-footer" className="bg-espresso px-6 py-16 text-ivory md:px-12 md:py-24">
      <div className="mx-auto max-w-[1600px]">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-4">
            <img src={IMAGES.logos.white} alt="Cabo Yoga & Fitness" className="h-12 w-auto" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-ivory/60">{t.footer.tagline}</p>
            <div className="mt-10 border-t border-ivory/15 pt-6">
              <p className="label-caps text-gold">{t.footer.officialDistributor}</p>
              <img src={IMAGES.lululemon} alt="lululemon" className="mt-4 h-7 w-auto" />
            </div>
          </div>

          <nav className="grid grid-cols-2 gap-x-10 gap-y-4 sm:max-w-sm lg:col-span-2 lg:block lg:space-y-4" aria-label="Footer">
            {NAV_LINKS.map((link) => (
              <a key={link.href} href={link.href.startsWith("#") ? `/${link.href}` : link.href} data-testid={`footer-link-${link.key}`}
                onClick={(event) => { event.preventDefault(); goToLink(link, { pathname, navigate }); }}
                className="u-link label-caps text-ivory/70 hover:text-ivory">
                {t.nav[link.key]}
              </a>
            ))}
            <a href="/booking#cancellation-policy" data-testid="footer-cancellation-policy-link" className="u-link col-span-2 label-caps text-ivory/70 hover:text-ivory lg:col-auto">
              {t.booking.cancellation.title}
            </a>
          </nav>

          <div className="lg:col-span-3">
            <p className="label-caps text-gold">{t.footer.visit}</p>
            <a href={contact.maps} target="_blank" rel="noopener noreferrer" className="group mt-5 flex max-w-xs gap-3 text-sm leading-relaxed text-ivory/70 hover:text-ivory">
              <MapPin size={17} strokeWidth={1.25} className="mt-0.5 shrink-0 text-gold" />
              <span>{contact.address.map((line) => <span key={line} className="block">{line}</span>)}</span>
            </a>
            <a href={contact.maps} target="_blank" rel="noopener noreferrer" className="u-link mt-4 label-caps text-ivory/55 hover:text-ivory">
              {t.footer.directions}<ArrowUpRight size={13} strokeWidth={1.5} className="u-arrow" />
            </a>
          </div>

          <div className="lg:col-span-3">
            <p className="label-caps text-gold">{t.footer.contact}</p>
            <div className="mt-5 space-y-3">
              <a href={`tel:${contact.phoneE164}`} className="u-link text-sm text-ivory/70 hover:text-ivory"><Phone size={14} strokeWidth={1.25} />{contact.phone}</a>
              <a href={`mailto:${contact.email}`} data-testid="footer-contact-link" className="u-link text-sm text-ivory/70 hover:text-ivory"><Mail size={14} strokeWidth={1.25} />{contact.email}</a>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-5 gap-y-3">
              <a href={contact.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="u-link label-caps text-ivory/70 hover:text-ivory"><Instagram size={14} strokeWidth={1.25} />Instagram</a>
              <a href={contact.facebook} target="_blank" rel="noopener noreferrer" className="u-link label-caps text-ivory/70 hover:text-ivory"><Facebook size={14} strokeWidth={1.25} />Facebook</a>
              <a href={contact.whatsapp} target="_blank" rel="noopener noreferrer" className="u-link label-caps text-ivory/70 hover:text-ivory"><MessageCircle size={14} strokeWidth={1.25} />WhatsApp</a>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-5 border-t border-ivory/15 pt-8 md:flex-row md:items-center md:justify-between">
          <p className="label-caps text-ivory/40">{t.footer.location}</p>
          <a href={BOOKING_URL} data-testid="footer-book-button" className="btn-pill btn-outline-light shrink-0">{t.nav.book}</a>
          <p className="label-caps text-ivory/40">© {new Date().getFullYear()} Cabo Yoga &amp; Fitness</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
